<template>
  <v-row no-gutters class="quiz mb-4 mt-2 class-quiz-report">
    <h5 class="text-center quiz-header">{{ title }}</h5>
    <div class="legend">
      <div class="legend-incorrect"><span /> <em>Incorrect</em></div>
      <div class="legend-correct"><span /> <em>Correct</em></div>
    </div>
    <v-col cols="12" md="6">
      <div class="chart-sizing donut">
        <pie-chart ref="quizDonutChart" :chart-data="donutChart" />
      </div>
      <p class="text-center mt-2">
        {{ title }} score distribution for the class
      </p>
    </v-col>
    <v-col cols="12" md="6">
      <div class="chart-sizing stacked">
        <stacked-bar-chart
          ref="quizStackedChart"
          :chart-data="stackedChart"
          v-on:handleClickOnBarGraph="showQuizQuestion"
        />
      </div>
      <p class="text-center mt-2">{{ title }} score distribution by question</p>
    </v-col>
    <class-quiz-report-dialog
      :dialog.sync="dialog"
      :questionIndex="questionIndex"
      :quizzes="quizzes"
    />
  </v-row>
</template>

<script>
import PieChart from '@/components/charts/PieChart'
import StackedBarChart from '@/components/charts/StackedBarChart'
import ClassQuizReportDialog from './ClassQuizReportDialog'

export default {
  name: 'ClassQuizReport',
  components: {
    PieChart,
    StackedBarChart,
    ClassQuizReportDialog
  },
  props: {
    title: {
      type: String,
      default: 'Quiz'
    },
    type: {
      type: String,
      validator: val => ['pre_quiz', 'post_quiz'].includes(val)
    },
    lesson: {
      type: Object,
      require: true
    },
    userLessons: {
      type: Array,
      require: true
    }
  },
  data() {
    return {
      dialog: false,
      questionIndex: null,
      stackedChart: {
        labels: ['No Data'],
        datasets: [
          {
            type: 'bar',
            label: 'Correct',
            backgroundColor: this.$vuetify.theme.themes.light.correct,
            data: [0]
          },
          {
            type: 'bar',
            label: 'Incorrect',
            backgroundColor: this.$vuetify.theme.themes.light.incorrect,
            data: [0]
          }
        ]
      },
      donutChart: {
        datasets: [
          {
            data: [0, 0],
            backgroundColor: [
              this.$vuetify.theme.themes.light.correct,
              this.$vuetify.theme.themes.light.incorrect
            ]
          }
        ],
        labels: ['Correct', 'Incorrect']
      }
    }
  },
  computed: {
    quizzes() {
      if (!this.lesson) return []
      return this.lesson.lesson_slides.filter(ul => ul.name === this.type)
    }
  },
  methods: {
    updateCharts() {
      this.computeDataForDonutChart()
      this.computeDataForStackedChart()
      setTimeout(() => {
        this.$refs.quizDonutChart.refresh()
        this.$refs.quizStackedChart.refresh()
      }, 100)
    },
    computeDataForStackedChart() {
      let datasetCorect = [],
        datasetIncorect = [],
        usersQuizCorrect = []
      let totalUsers = 0
      let labels = []
      let quizLessonCount = this.lesson[`${this.type}_count`]

      for (let i = 0; i < quizLessonCount; i++) {
        usersQuizCorrect.push(0)
        labels.push(`Question ${i + 1}`)
      }
      this.userLessons.forEach(userLesson => {
        if (userLesson.lesson_id === this.lesson.id) {
          let quizCounter = 0
          totalUsers++
          userLesson.user_lesson_slides.forEach(slide => {
            if (slide.lesson_slide.name === this.type) {
              quizCounter++
              if (slide.answer.is_correct) {
                usersQuizCorrect[quizCounter - 1] += 1
              }
            }
          })
        }
      })
      for (let i = 0; i < quizLessonCount; i++) {
        datasetCorect[i] = ((usersQuizCorrect[i] / totalUsers) * 100).toFixed(2)
        datasetIncorect[i] = (100 - datasetCorect[i]).toFixed(2)
      }

      this.stackedChart = {
        labels: labels,
        datasets: [
          {
            type: 'bar',
            label: 'Correct',
            backgroundColor: this.$vuetify.theme.themes.light.correct,
            data: datasetCorect
          },
          {
            type: 'bar',
            label: 'Incorrect',
            backgroundColor: this.$vuetify.theme.themes.light.incorrect,
            data: datasetIncorect
          }
        ]
      }
    },
    computeDataForDonutChart() {
      let quizCorrectTotal = 0
      let totalUsers = 0
      this.userLessons.forEach(userLesson => {
        if (userLesson.lesson_id === this.lesson.id) {
          quizCorrectTotal += userLesson[this.type]
          totalUsers++
        }
      })
      const correctQuizPercentage =
        (quizCorrectTotal / (totalUsers * this.lesson[`${this.type}_count`])) *
        100
      const incorrectQuizPercentage = 100 - correctQuizPercentage
      this.donutChart = {
        datasets: [
          {
            data: [correctQuizPercentage, incorrectQuizPercentage],
            backgroundColor: [
              this.$vuetify.theme.themes.light.correct,
              this.$vuetify.theme.themes.light.incorrect
            ]
          }
        ],
        labels: ['Correct', 'Incorrect']
      }
    },
    showQuizQuestion(questionIndex) {
      this.questionIndex = questionIndex
      this.dialog = true
    }
  }
}
</script>

<style lang="scss">
.class-quiz-report {
  background: #f1f1f1;
  padding: 10px 20px;

  .quiz-header {
    width: 100%;
    line-height: 30px;
    font-size: 18px;
  }
  .chart-sizing {
    &.donut {
      width: 300px;
      margin: 0 auto;
    }

    &.stacked {
      margin: auto;
    }
  }

  .legend {
    float: right;
    display: block;
    overflow: hidden;
    height: 25px;
    width: 100%;
    margin: 15px 0;

    > div {
      margin-left: 15px;
      float: right;

      &.legend-correct {
        span {
          background-color: var(--v-correct-base);
        }
      }

      &.legend-incorrect {
        span {
          background-color: var(--v-incorrect-base);
        }
      }

      span {
        width: 15px;
        height: 15px;
        display: inline-block;
        vertical-align: middle;
      }

      em {
        font-style: normal;
        margin-left: 5px;
        display: inline-block;
        vertical-align: middle;
      }
    }
  }
}
</style>
